const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

class ApiClient {
  constructor(baseURL) {
    this.baseURL = baseURL;
  }

  getToken() {
    return localStorage.getItem('token');
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const token = this.getToken();

    const config = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
    };

    if (options.body && typeof options.body === 'object') {
      config.body = JSON.stringify(options.body);
    }

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401 && !endpoint.startsWith('/auth/')) {
          localStorage.removeItem('token');
          window.location.href = '/login';
        }
        throw { response: { status: response.status, data } };
      }

      return { data };
    } catch (error) {
      if (error.response) {
        throw error;
      }
      throw { response: { status: 500, data: { error: 'Network error' } } };
    }
  }

  get(endpoint) {
    return this.request(endpoint, { method: 'GET' });
  }

  post(endpoint, data) {
    return this.request(endpoint, { method: 'POST', body: data });
  }

  put(endpoint, data) {
    return this.request(endpoint, { method: 'PUT', body: data });
  }

  delete(endpoint, data) {
    return this.request(endpoint, { method: 'DELETE', body: data });
  }
}

const api = new ApiClient(`${API_URL}/api`);

// Auth APIs
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout', {}),
  getCurrentUser: () => api.get('/auth/me'),
  refreshToken: () => api.post('/auth/refresh', {}),
  updateProfile: (data) => api.put('/auth/profile', data),
  changePassword: (data) => api.put('/auth/password', data),
  verifyEmail: (token) => api.post('/auth/verify-email', { token }),
  resendVerification: () => api.post('/auth/resend-verification', {}),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }),
  deleteAccount: (password) => api.delete('/auth/account', { password }),
};

// OAuth APIs
export const oauthAPI = {
  getStatus: () => api.get('/oauth/status'),
  getMicrosoftAuthUrl: () => api.get('/oauth/microsoft/authorize'),
  getGoogleAuthUrl: () => api.get('/oauth/google/authorize'),
  disconnect: (provider) => api.delete(`/oauth/${provider}/disconnect`),
};

// Tenant APIs
export const tenantAPI = {
  getCurrent: () => api.get('/tenant'),
  update: (data) => api.put('/tenant', data),
};

// Plan APIs
export const planAPI = {
  getAll: () => api.get('/plans'),
  getById: (id) => api.get(`/plans/${id}`),
  activate: (data) => api.post('/plans/activate', data),
  getCurrentSubscription: () => api.get('/plans/subscription/current'),
};

// Microsoft APIs (Milestone 2)
export const microsoftAPI = {
  // MFA endpoints
  getMFAUsers: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return api.get(`/microsoft/mfa/users${query ? `?${query}` : ''}`);
  },
  getMFAUser: (userId) => api.get(`/microsoft/mfa/users/${userId}`),
  getMFASummary: () => api.get('/microsoft/mfa/summary'),

  // Intune status check
  checkIntuneStatus: () => api.get('/microsoft/intune/status'),

  // Device endpoints
  getDevices: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return api.get(`/microsoft/devices${query ? `?${query}` : ''}`);
  },
  getDevice: (deviceId) => api.get(`/microsoft/devices/${deviceId}`),
  getDeviceSummary: () => api.get('/microsoft/devices/summary'),

  // Sync endpoints
  triggerSync: (type = 'full') => api.post('/microsoft/sync', { type }),
  getSyncStatus: () => api.get('/microsoft/sync/status'),

  // Security endpoints (Milestone 4)
  getSecurityAlerts: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return api.get(`/microsoft/security/alerts${query ? `?${query}` : ''}`);
  },
  getSecurityAlert: (alertId) => api.get(`/microsoft/security/alerts/${alertId}`),
  getSecurityAlertsSummary: () => api.get('/microsoft/security/alerts/summary'),
  getRiskyUsers: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return api.get(`/microsoft/security/risky-users${query ? `?${query}` : ''}`);
  },
  getRiskyUsersSummary: () => api.get('/microsoft/security/risky-users/summary'),
  getRiskySignins: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return api.get(`/microsoft/security/risky-signins${query ? `?${query}` : ''}`);
  },
  getRiskySigninsSummary: () => api.get('/microsoft/security/risky-signins/summary'),
  getSecuritySummary: () => api.get('/microsoft/security/summary'),
  triggerSecuritySync: () => api.post('/microsoft/security/sync', {}),

  // File Sharing endpoints (Milestone 5)
  getSharingFiles: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return api.get(`/microsoft/sharing/files${query ? `?${query}` : ''}`);
  },
  getSharingSummary: () => api.get('/microsoft/sharing/summary'),
  triggerSharingSync: () => api.post('/microsoft/sharing/sync', {}),
};

// Google APIs (Milestone 3)
export const googleAPI = {
  // MFA (2SV) endpoints
  getMFAUsers: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return api.get(`/google/mfa/users${query ? `?${query}` : ''}`);
  },
  getMFAUser: (userId) => api.get(`/google/mfa/users/${userId}`),
  getMFASummary: () => api.get('/google/mfa/summary'),

  // Device endpoints
  getDevices: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return api.get(`/google/devices${query ? `?${query}` : ''}`);
  },
  getDevice: (deviceId) => api.get(`/google/devices/${deviceId}`),
  getDeviceSummary: () => api.get('/google/devices/summary'),

  // Sync endpoints
  triggerSync: (type = 'full') => api.post('/google/sync', { type }),
  getSyncStatus: () => api.get('/google/sync/status'),

  // Admin status check
  checkAdminStatus: () => api.get('/google/admin/status'),

  // File Sharing endpoints (Milestone 5)
  getSharingFiles: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return api.get(`/google/sharing/files${query ? `?${query}` : ''}`);
  },
  getSharingSummary: () => api.get('/google/sharing/summary'),
  triggerSharingSync: () => api.post('/google/sharing/sync', {}),
};

// Billing APIs (Milestone 6)
export const billingAPI = {
  // Plans
  getPlans: () => api.get('/billing/plans'),

  // Subscription
  getSubscription: () => api.get('/billing/subscription'),
  updateSubscription: (data) => api.put('/billing/subscription', data),
  cancelSubscription: (data) => api.delete('/billing/subscription', data),
  reactivateSubscription: () => api.post('/billing/subscription/reactivate', {}),

  // Checkout and Portal
  createCheckout: (data) => api.post('/billing/checkout', data),
  createPortalSession: () => api.post('/billing/portal', {}),

  // Payments and Invoices
  getPayments: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return api.get(`/billing/payments${query ? `?${query}` : ''}`);
  },
  getInvoices: () => api.get('/billing/invoices'),

  // Coupon validation
  validateCoupon: (data) => api.post('/billing/coupon/validate', data),

  // Preview changes
  getPreview: (data) => api.post('/billing/preview', data),

  // Contact sales (Enterprise)
  contactSales: (data) => api.post('/billing/contact-sales', data),
};

// Team APIs (Milestone 7)
export const teamAPI = {
  // Team members
  getMembers: () => api.get('/team/members'),
  getLimits: () => api.get('/team/limits'),
  removeMember: (memberId) => api.delete(`/team/members/${memberId}`),
  updateMemberRole: (memberId, role) => api.put(`/team/members/${memberId}/role`, { role }),
  transferOwnership: (userId) => api.post('/team/transfer-ownership', { userId }),

  // Invitations
  getInvitations: (status) => {
    const query = status ? `?status=${status}` : '';
    return api.get(`/team/invitations${query}`);
  },
  sendInvitation: (email, role = 'member') => api.post('/team/invite', { email, role }),
  revokeInvitation: (invitationId) => api.delete(`/team/invitations/${invitationId}`),
  resendInvitation: (invitationId) => api.post(`/team/invitations/${invitationId}/resend`, {}),

  // Public invitation endpoints (no auth required)
  getInvitationByToken: (token) => api.get(`/team/invitations/token/${token}`),
  acceptInvitation: (token) => api.post(`/team/invitations/token/${token}/accept`, {}),
  acceptInvitationWithRegistration: (token, data) =>
    api.post(`/team/invitations/token/${token}/register`, data),

  // Features
  getFeatures: () => api.get('/team/features'),
};

// Remediation API
export const remediationAPI = {
  // Get all remediation items
  getItems: () => api.get('/remediation'),

  // Get summary statistics
  getSummary: () => api.get('/remediation/summary'),

  // Get Cyber Essentials mapping
  getCyberEssentialsMapping: () => api.get('/remediation/cyber-essentials'),

  // Request professional help
  requestHelp: (itemId, data) => api.post('/remediation/request-help', { itemId, ...data }),

  // --- Phase 2 (CyberActions) automated remediation engine ---

  // Current backend posture score
  getScore: () => api.get('/remediation/score'),

  // Build plan + summary + pre-flight for the confirmation modal
  getPlan: (minSeverity = 'high') => api.get(`/remediation/plan?minSeverity=${minSeverity}`),

  // Dry run (no writes)
  preview: (minSeverity = 'high') => api.post('/remediation/preview', { minSeverity }),

  // "Fix It All" — queue a real apply job (admin only)
  run: (minSeverity = 'high') => api.post('/remediation/run', { minSeverity }),

  // Jobs
  listJobs: () => api.get('/remediation/jobs'),
  getJob: (id) => api.get(`/remediation/jobs/${id}`),

  // Audit trail
  getAuditLog: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return api.get(`/remediation/audit${qs ? `?${qs}` : ''}`);
  },

  // Download the audit trail export (csv | json | pdf) with compliance rollup.
  // Uses window.open with ?token= since downloads can't set an Authorization header.
  downloadAuditExport: (format = 'csv', jobId) => {
    const token = api.getToken();
    const params = new URLSearchParams({ format, token });
    if (jobId) params.set('jobId', jobId);
    window.open(`${api.baseURL}/remediation/audit/export?${params.toString()}`, '_blank');
  },

  // Rollback (M2)
  rollbackJob: (id) => api.post(`/remediation/jobs/${id}/rollback`),
  rollbackAction: (id) => api.post(`/remediation/actions/${id}/rollback`),
};

// Reports & Export API
export const reportsAPI = {
  // Download Cyber Essentials PDF report
  downloadCEReport: () => {
    const token = api.getToken();
    window.open(`${api.baseURL}/reports/cyber-essentials?token=${token}`, '_blank');
  },

  // Export MFA data to CSV
  exportMFA: () => {
    const token = api.getToken();
    window.open(`${api.baseURL}/reports/export/mfa?token=${token}`, '_blank');
  },

  // Export devices to CSV
  exportDevices: () => {
    const token = api.getToken();
    window.open(`${api.baseURL}/reports/export/devices?token=${token}`, '_blank');
  },

  // Export security alerts to CSV
  exportAlerts: () => {
    const token = api.getToken();
    window.open(`${api.baseURL}/reports/export/alerts?token=${token}`, '_blank');
  },

  // Export file sharing data to CSV
  exportFileSharing: () => {
    const token = api.getToken();
    window.open(`${api.baseURL}/reports/export/file-sharing?token=${token}`, '_blank');
  },
};

// Tasks API (Phase 6)
export const tasksAPI = {
  // Categories
  getCategories: () => api.get('/tasks/categories'),

  // Tasks
  getTasks: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return api.get(`/tasks${query ? `?${query}` : ''}`);
  },
  getTask: (id) => api.get(`/tasks/${id}`),
  getSummary: () => api.get('/tasks/summary'),
  updateTask: (id, data) => api.put(`/tasks/${id}`, data),
  assignTask: (id, userId) => api.put(`/tasks/${id}/assign`, { userId }),

  // Team members for assignment
  getTeamMembers: () => api.get('/tasks/team'),

  // Auto-checks
  runAutoChecks: (force = false) => api.post('/tasks/auto-check', { force }),
  getAutoCheckTypes: () => api.get('/tasks/auto-check/types'),
};

// Notifications API (Phase 7)
export const notificationsAPI = {
  // List notifications
  getNotifications: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return api.get(`/notifications${query ? `?${query}` : ''}`);
  },

  // Get unread count
  getUnreadCount: () => api.get('/notifications/unread-count'),

  // Mark as read
  markAsRead: (id) => api.put(`/notifications/${id}/read`, {}),

  // Mark all as read
  markAllAsRead: () => api.put('/notifications/read-all', {}),

  // Delete notification
  delete: (id) => api.delete(`/notifications/${id}`),

  // Manual triggers
  triggerMFAAlert: () => api.post('/notifications/trigger/mfa-alert', {}),
  triggerWeeklySummary: () => api.post('/notifications/trigger/weekly-summary', {}),

  // Preferences
  getPreferences: () => api.get('/notifications/preferences'),
  updatePreferences: (data) => api.put('/notifications/preferences', data),
};

// API Keys API (Phase 8)
export const apiKeysAPI = {
  // List all API keys
  list: () => api.get('/api-keys'),

  // Create a new API key
  create: (data) => api.post('/api-keys', data),

  // Get single API key
  get: (id) => api.get(`/api-keys/${id}`),

  // Update API key
  update: (id, data) => api.put(`/api-keys/${id}`, data),

  // Revoke (delete) API key
  revoke: (id) => api.delete(`/api-keys/${id}`),

  // Get available scopes
  getScopes: () => api.get('/api-keys/scopes'),
};

// Branding API (Phase 8)
export const brandingAPI = {
  // Get branding
  get: () => api.get('/branding'),

  // Update branding
  update: (data) => api.put('/branding', data),

  // Reset to defaults
  reset: () => api.delete('/branding'),

  // Upload logo (uses FormData)
  uploadLogo: async (file, type = 'logo') => {
    const formData = new FormData();
    formData.append('file', file);

    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/api/branding/logo?type=${type}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    const result = await response.json();
    if (!response.ok) {
      throw { response: { status: response.status, data: result } };
    }
    return { data: result };
  },

  // Remove logo
  removeLogo: (type = 'logo') => api.delete(`/branding/logo?type=${type}`),
};

// MSP API (Phase 8)
export const mspAPI = {
  // Dashboard
  getDashboard: () => api.get('/msp'),
  getStats: () => api.get('/msp/stats'),

  // MSP mode
  enable: () => api.post('/msp/enable', {}),
  disable: () => api.post('/msp/disable', {}),

  // Client management
  getClients: () => api.get('/msp/clients'),
  createClient: (data) => api.post('/msp/clients', data),
  getClient: (id) => api.get(`/msp/clients/${id}`),
  removeClient: (id) => api.delete(`/msp/clients/${id}`),

  // Impersonation
  impersonate: (clientId) => api.post(`/msp/clients/${clientId}/impersonate`, {}),

  // Bulk remediation fan-out across all client tenants (preview | apply)
  bulkRemediate: (mode = 'preview', minSeverity = 'high') =>
    api.post('/msp/bulk-remediate', { mode, minSeverity }),

  // Download a per-client Cyber Essentials report (opens in a new tab).
  // Ownership is validated server-side against the calling MSP's children.
  downloadClientReport: (clientId) => {
    const token = api.getToken();
    const params = new URLSearchParams({ token, tenantId: clientId });
    window.open(`${api.baseURL}/reports/cyber-essentials?${params.toString()}`, '_blank');
  },
};

// Evidence API (Phase 6)
export const evidenceAPI = {
  // List and storage
  getEvidence: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return api.get(`/evidence${query ? `?${query}` : ''}`);
  },
  getStorageUsage: () => api.get('/evidence/storage'),

  // Upload (uses FormData, not JSON)
  upload: async (file, data = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    if (data.taskId) formData.append('taskId', data.taskId);
    if (data.description) formData.append('description', data.description);

    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/api/evidence/upload`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    const result = await response.json();
    if (!response.ok) {
      throw { response: { status: response.status, data: result } };
    }
    return { data: result };
  },

  // Single evidence operations
  getById: (id) => api.get(`/evidence/${id}`),
  update: (id, data) => api.put(`/evidence/${id}`, data),
  linkToTask: (id, taskId) => api.put(`/evidence/${id}/link`, { taskId }),
  delete: (id) => api.delete(`/evidence/${id}`),

  // Download
  download: (id) => {
    const token = localStorage.getItem('token');
    window.open(`${API_URL}/api/evidence/${id}/download?token=${token}`, '_blank');
  },
};

// Export API_URL for components that need to construct URLs (e.g., for images)
export { API_URL };

export default api;
